import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, type OnInit, output, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DividerModule } from '@openng/optimus-ui/divider';
import { InplaceModule } from '@openng/optimus-ui/inplace';
import { TagModule } from '@openng/optimus-ui/tag';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { sortBy } from 'lodash-es';
import { injectParams } from 'ngxtension/inject-params';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { LearningHubService } from '@app/modules/learning-hub/services/learning-hub.service';
import { TourService } from '@app/modules/learning-hub/services/tour.service';
import { ProfilePopoverComponent } from '@app/shared/components/profile-popover/profile-popover.component';
import { ProjectListComponent } from '@app/shared/components/project-list/project-list.component';
import { MENU_ROUTES } from '@app/shared/constants/menu-routes.config';
import {
    HIDDEN_MODULE_PAGES_MAP,
    MODULE_DISPLAY_NAME,
    MODULE_PAGES_MAP,
    MODULE_ROUTE_PATH,
    MODULES,
    PAGE_INFO,
    PAGE_ROUTE_MAP,
    PAGES,
} from '@app/shared/constants/shared.constant';
import type { TabMenu } from '@app/shared/interface/common.interface';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { StorageService } from '@app/shared/services/storage.service';
import { ThemeService } from '@app/shared/services/theme.service';
import { Helpers } from '@app/shared/utils/helpers';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import { RouterHelper } from '@app/shared/utils/router-helper';

interface Card {
    module?: MODULES;
    name: string;
    headerOrder?: number;
    tabs: TabMenu[];
}

@Component({
    selector: 'cor-sidebar-nav',
    templateUrl: './sidebar-nav.component.html',
    imports: [
        RouterLink,
        RouterLinkActive,
        TooltipModule,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        ButtonModule,
        ProfilePopoverComponent,
        DividerModule,
        InplaceModule,
        TagModule,
        ProjectListComponent,
    ],
})
export class SidebarNavComponent implements OnInit {
    onClose = output();
    readonly showAsOverlay = input(false); // Whether to show the sidebar as an overlay

    // By default, the sidebar is NOT collapsed - i.e. it is open
    protected readonly collapsed = signal(false); // Collapse sidepanel state currently

    protected readonly project = {
        currentProject: injectParams<string>('projectSlug'),
        onSelect: (slug: string) => {
            const oldSlug = this.project.currentProject();
            if (oldSlug && slug) {
                this.router.navigateByUrl(this.router.url.replace(`/${oldSlug}`, `/${slug}`));
            } else if (oldSlug && !slug) {
                const currentRoute = this.route.firstChild?.routeConfig;
                // In themeRoutes, the empty-path theme route contains the sections
                // available without a project slug. Use it as the source of truth.
                // Check if current route is present without the project slug and redirect based on that
                const allProjectsRoutes = this.router.config.find(
                    route => route.path === '',
                )?.children;
                const availableInAllProjects = allProjectsRoutes?.some(
                    route => route.path === currentRoute?.path,
                );
                this.router.navigateByUrl(
                    availableInAllProjects
                        ? this.router.url.replace(`/${oldSlug}`, '')
                        : `/${MODULE_ROUTE_PATH.HOME}`,
                );
            } else if (!oldSlug && slug) {
                this.router.navigateByUrl(`/${slug}${this.router.url}`);
            }
        },
    };

    protected MODULES = MODULES;
    protected MODULE_ROUTE_PATH = MODULE_ROUTE_PATH;
    protected TAB_ROUTES = TAB_ROUTES;

    protected appConfigService = inject(AppConfigService);
    protected readonly themeService = inject(ThemeService);
    protected readonly learningHubService = inject(LearningHubService);
    private readonly authService = inject(AuthenticationService);
    protected readonly permissionService = inject(PermissionService);
    protected readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly storageService = inject(StorageService);

    protected readonly tourService = inject(TourService);
    private readonly moduleCards = computed(() => {
        this.permissionService.currentProjectPermissions(); // If permissions change, rerun this

        return MENU_ROUTES.filter(card =>
            this.permissionService.canAccessModulePage(card.module),
        ).map(card => ({ ...card, name: MODULE_DISPLAY_NAME[card.module] }));
    });
    protected readonly cards = computed((): Card[] => {
        this.project.currentProject(); // Re-evaluate when projectSlug changes
        const moduleCards = sortBy(
            this.moduleCards().map(card => ({ ...card, tabs: this.getTabsForModule(card.module) })),
            'headerOrder',
        );
        const projectManagementCard = this.project.currentProject()
            ? {
                  name: 'Manage Project',
                  tabs: [
                      {
                          name: PAGE_INFO[PAGES.PROJECT_SETTINGS].name,
                          page: PAGES.PROJECT_SETTINGS,
                          path: `/${this.project.currentProject()}/${PAGE_ROUTE_MAP[PAGES.PROJECT_SETTINGS]}`,
                          active: false,
                          tooltip: PAGE_INFO[PAGES.PROJECT_SETTINGS].tooltip
                              ? PAGE_INFO[PAGES.PROJECT_SETTINGS].tooltip
                              : undefined,
                          icon: PAGE_INFO[PAGES.PROJECT_SETTINGS].icon
                              ? PAGE_INFO[PAGES.PROJECT_SETTINGS].icon
                              : undefined,
                          visible: this.permissionService.canAccessModulePage(
                              PAGES.PROJECT_SETTINGS,
                          ),
                      },
                  ],
              }
            : null;
        const cards = projectManagementCard ? [...moduleCards, projectManagementCard] : moduleCards;

        return cards.filter(card => card.tabs.some(tab => tab.visible));
    });

    ngOnInit() {
        // Collapse the sidebar based on localstorage
        const currentState =
            this.storageService.getLocalStorage(`sidebar-collapsed-state`) || 'open';
        this.collapsed.set(currentState !== 'open');

        this.authService.registerTokenListener();
    }

    protected getTabsForModule(module: MODULES): TabMenu[] {
        const modulePages = [...MODULE_PAGES_MAP[module]];

        return modulePages.map(page => {
            return {
                name: (page in PAGE_INFO ? PAGE_INFO[page].name : page) as PAGES,
                page,
                module,
                path: RouterHelper.getPageRoute(page, this.project.currentProject()),
                active: false,
                tooltip:
                    page in PAGE_INFO && PAGE_INFO[page].tooltip
                        ? PAGE_INFO[page].tooltip
                        : undefined,
                icon: page in PAGE_INFO && PAGE_INFO[page].icon ? PAGE_INFO[page].icon : undefined,
                hidden: HIDDEN_MODULE_PAGES_MAP[module]?.includes(page),
                visible: this.permissionService.canAccessModulePage(
                    Helpers.getPermissionName(module, page),
                ),
            };
        });
    }

    protected toggleCollapsing() {
        this.collapsed.update(x => !x);
        this.storageService.setLocalStorage(
            `sidebar-collapsed-state`,
            this.collapsed() ? 'closed' : 'open',
        );
        // trigger a window resize
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 400);
    }
}
